import { useState, useEffect } from 'react';
import api from '@/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Landmark, CreditCard, Eye, EyeOff, Pencil, Send, Trash2, Loader2, ShieldAlert, CheckCircle2, AlertCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';

const CARD_BRANDS = ['Visa', 'Mastercard', 'American Express', 'Discover'];

export default function BillingInfoCard({ customerId, currentUser }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [showSendLink, setShowSendLink] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');

  const isAdmin = currentUser?.role === 'master_admin' || currentUser?.role === 'admin';
  const canEdit = ['master_admin', 'admin', 'accounting', 'operator'].includes(currentUser?.role);

  function emptyForm() {
    return {
      bank_name: '', account_holder_name: '', routing_number: '', account_number: '',
      card_on_file: false, card_holder_name: '', card_brand: '', card_last4: '', card_expiry: '', card_billing_zip: '',
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/billing/customer/${customerId}`);
      setInfo(r.data);
    } catch (e) {
      // silent — could be 403 for non-billing roles
      setInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (customerId) load(); }, [customerId]);

  const openEdit = () => {
    if (info?.status === 'COMPLETE') {
      setForm({
        bank_name: info.bank_name || '',
        account_holder_name: info.account_holder_name || '',
        routing_number: info.routing_number || '',
        account_number: '', // never pre-fill (encrypted)
        card_on_file: !!info.card_on_file,
        card_holder_name: info.card_holder_name || '',
        card_brand: info.card_brand || '',
        card_last4: info.card_last4 || '',
        card_expiry: info.card_expiry || '',
        card_billing_zip: info.card_billing_zip || '',
      });
    } else {
      setForm(emptyForm());
    }
    setShowEdit(true);
  };

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const saveForm = async () => {
    if (!form.bank_name.trim() || !form.account_holder_name.trim()) {
      toast.error('Bank name and account holder are required'); return;
    }
    if (!/^\d{9}$/.test(form.routing_number)) { toast.error('Routing number must be 9 digits'); return; }
    if (!/^[0-9-\s]{4,20}$/.test(form.account_number)) { toast.error('Enter the full account number (numeric)'); return; }
    if (form.card_on_file) {
      if (!/^\d{4}$/.test(form.card_last4)) { toast.error('Card last 4 must be 4 digits'); return; }
      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(form.card_expiry)) { toast.error('Expiry must be MM/YY'); return; }
    }
    setSaving(true);
    try {
      const payload = {
        bank_name: form.bank_name.trim(),
        account_holder_name: form.account_holder_name.trim(),
        routing_number: form.routing_number.trim(),
        account_number: form.account_number.replace(/[\s-]/g, ''),
        card_on_file: form.card_on_file,
        ...(form.card_on_file && {
          card_holder_name: form.card_holder_name.trim(),
          card_brand: form.card_brand,
          card_last4: form.card_last4.trim(),
          card_expiry: form.card_expiry.trim(),
          card_billing_zip: form.card_billing_zip.trim(),
        }),
      };
      await api.post(`/billing/customer/${customerId}`, payload);
      toast.success('Billing info saved');
      setShowEdit(false);
      setRevealed(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const reveal = async () => {
    if (!isAdmin) { toast.error('Only admins can reveal account numbers'); return; }
    setRevealing(true);
    try {
      const r = await api.post(`/billing/customer/${customerId}/reveal-account`);
      setRevealed(r.data.account_number);
      toast.success('Account number revealed — this action was audit-logged');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reveal failed');
    } finally {
      setRevealing(false);
    }
  };

  const removeCard = async () => {
    if (!window.confirm('Remove card on file? This cannot be undone.')) return;
    try {
      await api.delete(`/billing/customer/${customerId}/card`);
      toast.success('Card on file removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove card');
    }
  };

  const sendLink = async () => {
    setSendingLink(true);
    try {
      const r = await api.post(`/billing/customer/${customerId}/send-link`, linkEmail ? { email: linkEmail } : {});
      setGeneratedLink(r.data.link);
      toast.success(`Setup link sent to ${r.data.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send link');
    } finally {
      setSendingLink(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="card-slate p-5" data-testid="billing-info-card">
        <div className="flex items-center gap-2 mb-3">
          <Landmark size={16} style={{ color: 'var(--text-muted)' }} />
          <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Billing Information</p>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-faint)' }}>Loading...</p>
      </div>
    );
  }

  const hasInfo = info?.status === 'COMPLETE';

  return (
    <div className="card-slate p-5" data-testid="billing-info-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Landmark size={16} style={{ color: 'var(--text-muted)' }} />
          <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Billing Information</p>
          {hasInfo ? (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm flex items-center gap-1" style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}>
              <CheckCircle2 size={9} /> On File
            </span>
          ) : (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              Not Set
            </span>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button onClick={() => { setLinkEmail(''); setGeneratedLink(''); setShowSendLink(true); }}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded-sm border transition-colors font-['IBM_Plex_Sans']"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
              data-testid="billing-send-link-btn">
              <Send size={12} /> Send Link
            </button>
            <button onClick={openEdit}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded-sm border transition-colors font-['IBM_Plex_Sans']"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
              data-testid="billing-edit-btn">
              <Pencil size={12} /> {hasInfo ? 'Edit' : 'Enter Manually'}
            </button>
          </div>
        )}
      </div>

      {!hasInfo ? (
        <div className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          <p>No billing info on file yet.</p>
          {info?.pending_link && (
            <p className="mt-2 flex items-center gap-1.5" style={{ color: 'var(--primary)' }}>
              <Send size={11} /> Setup link sent to <strong>{info.pending_link.email}</strong> — awaiting customer submission.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Bank/ACH */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
              <Landmark size={10} /> Bank / ACH
            </p>
            <div className="space-y-1.5">
              <Row label="Bank" value={info.bank_name} />
              <Row label="Name on Account" value={info.account_holder_name} />
              <Row label="Routing" value={info.routing_number} mono />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-faint)' }}>Account</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }} data-testid="billing-account-display">
                    {revealed || info.account_masked}
                  </span>
                  {isAdmin && (
                    revealed ? (
                      <button onClick={() => setRevealed(null)} title="Hide"
                        className="p-1 rounded hover:bg-white/5" data-testid="billing-hide-account-btn">
                        <EyeOff size={12} style={{ color: 'var(--text-muted)' }} />
                      </button>
                    ) : (
                      <button onClick={reveal} disabled={revealing} title="Reveal (audit logged)"
                        className="p-1 rounded hover:bg-white/5" data-testid="billing-reveal-account-btn">
                        {revealing ? <Loader2 size={12} className="animate-spin" style={{ color: 'var(--text-muted)' }} /> :
                          <Eye size={12} style={{ color: 'var(--primary)' }} />}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Card */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
              <CreditCard size={10} /> Card on File
            </p>
            {info.card_on_file ? (
              <div className="space-y-1.5">
                <Row label="Brand" value={info.card_brand} />
                <Row label="Number" value={`••••${info.card_last4}`} mono />
                <Row label="Cardholder" value={info.card_holder_name} />
                <Row label="Expiry" value={info.card_expiry} mono />
                <Row label="Billing ZIP" value={info.card_billing_zip} mono />
                {canEdit && (
                  <button onClick={removeCard}
                    className="mt-2 flex items-center gap-1 text-[11px] font-['IBM_Plex_Sans'] hover:underline"
                    style={{ color: 'var(--danger)' }} data-testid="billing-remove-card-btn">
                    <Trash2 size={10} /> Remove card
                  </button>
                )}
              </div>
            ) : (
              <p className="text-xs italic" style={{ color: 'var(--text-faint)' }}>No card on file</p>
            )}
          </div>
        </div>
      )}

      {hasInfo && (
        <div className="mt-4 pt-3 border-t flex items-center gap-2 text-[10px]" style={{ borderColor: 'var(--border)', color: 'var(--text-faint)' }}>
          <ShieldAlert size={11} />
          <span>Updated {info.updated_at ? new Date(info.updated_at).toLocaleDateString() : '—'} via {info.source === 'customer_link' ? 'customer link' : 'ops manual entry'}</span>
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="billing-edit-modal">
          <DialogHeader>
            <DialogTitle>{hasInfo ? 'Edit' : 'Enter'} Billing Information</DialogTitle>
            <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
              Bank info is encrypted at rest. Card info stores reference data only (last 4, brand, expiry).
            </p>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <section>
              <p className="text-[10px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--primary)' }}>Bank / ACH — Required</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Bank Name *" value={form.bank_name} onChange={v => upd('bank_name', v)} testid="edit-bank-name" />
                <Input label="Name on Account *" value={form.account_holder_name} onChange={v => upd('account_holder_name', v)} testid="edit-account-holder" />
                <Input label="Routing Number * (9 digits)" value={form.routing_number} onChange={v => upd('routing_number', v.replace(/\D/g, '').slice(0, 9))} testid="edit-routing" />
                <Input label="Account Number *" type="password" value={form.account_number} onChange={v => upd('account_number', v.replace(/[^\d-\s]/g, '').slice(0, 20))} placeholder={hasInfo ? 'Re-enter to update' : ''} testid="edit-account" />
              </div>
              {hasInfo && (
                <p className="text-[10px] mt-2" style={{ color: 'var(--text-faint)' }}>
                  ⚠ Re-enter the full account number to save changes (current value cannot be pre-filled for security).
                </p>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Card on File — Optional</p>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-card-toggle">
                  <input type="checkbox" checked={form.card_on_file} onChange={e => upd('card_on_file', e.target.checked)} className="w-4 h-4 accent-yellow-500" />
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Add a card</span>
                </label>
              </div>
              {form.card_on_file && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="Cardholder Name *" value={form.card_holder_name} onChange={v => upd('card_holder_name', v)} testid="edit-card-name" />
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Brand *</label>
                    <select value={form.card_brand} onChange={e => upd('card_brand', e.target.value)} data-testid="edit-card-brand"
                      className="w-full h-10 px-3 rounded-md text-sm focus:outline-none"
                      style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                      <option value="">Select...</option>
                      {CARD_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <Input label="Last 4 *" value={form.card_last4} onChange={v => upd('card_last4', v.replace(/\D/g, '').slice(0, 4))} placeholder="1234" testid="edit-card-last4" />
                  <Input label="Expiry MM/YY *" value={form.card_expiry} onChange={v => {
                    let s = v.replace(/\D/g, '').slice(0, 4);
                    if (s.length >= 3) s = s.slice(0, 2) + '/' + s.slice(2);
                    upd('card_expiry', s);
                  }} placeholder="08/27" testid="edit-card-expiry" />
                  <Input label="Billing ZIP *" value={form.card_billing_zip} onChange={v => upd('card_billing_zip', v.replace(/\D/g, '').slice(0, 5))} testid="edit-card-zip" />
                </div>
              )}
            </section>

            <div className="p-3 rounded-md flex items-start gap-2 text-[11px]" style={{ background: 'rgba(246,194,50,0.08)', border: '1px solid rgba(246,194,50,0.25)', color: 'var(--text-muted)' }}>
              <ShieldAlert size={13} style={{ color: 'var(--primary)' }} className="mt-0.5 flex-shrink-0" />
              <span>Never enter full card numbers or CVV. We collect last-4 reference data only — keeps us out of PCI scope.</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)} disabled={saving} data-testid="billing-cancel-btn">Cancel</Button>
            <Button onClick={saveForm} disabled={saving} data-testid="billing-save-btn">
              {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving...</> : 'Save Billing Info'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── SEND LINK MODAL ── */}
      <Dialog open={showSendLink} onOpenChange={(o) => { setShowSendLink(o); if (!o) setGeneratedLink(''); }}>
        <DialogContent data-testid="billing-send-link-modal">
          <DialogHeader>
            <DialogTitle>Send Billing Setup Link</DialogTitle>
            <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
              Sends the customer a secure one-time link (valid 7 days) to enter their billing info themselves.
            </p>
          </DialogHeader>

          {!generatedLink ? (
            <div className="space-y-3 py-2">
              <Input
                label="Email Address (leave blank to use customer's email on file)"
                value={linkEmail}
                onChange={v => setLinkEmail(v)}
                placeholder="customer@example.com"
                testid="billing-link-email"
              />
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="p-3 rounded-md flex items-start gap-2" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <CheckCircle2 size={14} style={{ color: '#22C55E' }} className="mt-0.5" />
                <p className="text-xs" style={{ color: 'var(--text-primary)' }}>
                  Link sent successfully. You can also share it directly:
                </p>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <span className="text-xs font-mono flex-1 truncate" style={{ color: 'var(--primary)' }}>{generatedLink}</span>
                <button onClick={copyLink} className="p-1.5 rounded hover:bg-white/5" title="Copy link" data-testid="billing-copy-link-btn">
                  <Copy size={13} style={{ color: 'var(--text-muted)' }} />
                </button>
              </div>
            </div>
          )}

          <DialogFooter>
            {!generatedLink ? (
              <>
                <Button variant="outline" onClick={() => setShowSendLink(false)}>Cancel</Button>
                <Button onClick={sendLink} disabled={sendingLink} data-testid="billing-confirm-send-link">
                  {sendingLink ? <><Loader2 size={14} className="animate-spin mr-2" /> Sending...</> : 'Send Link'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowSendLink(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-faint)' }}>{label}</span>
      <span className={`text-xs ${mono ? 'font-mono' : ''}`} style={{ color: 'var(--text-primary)' }}>
        {value || <span style={{ color: 'var(--text-faint)' }}>—</span>}
      </span>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder = '', testid }) {
  return (
    <div>
      <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full h-10 px-3 rounded-md text-sm focus:outline-none transition-colors"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
    </div>
  );
}
